import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_CURRENCY_CODE } from '@/utils/money';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { ItemSubtitle } from '@/components/item-subtitle';
import { List, Props as ListProps } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { Text, View } from 'react-native';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

export type SkuListOption = ListOption<{ sku: ItemListOptionMetadata }>;

type Props = Omit<ListProps<SkuListOption>, 'data'> & {
  itemMetadata: ItemListOptionMetadata;
};

export const UpdateOrderGuideItemSkuList: React.FC<Props> = ({
  itemMetadata,
  ...listProps
}) => {
  const { currencyCode } = useVariantPackInfoContext();
  const [styles] = useStyles();

  const skuSelectionListItems = useMemo<SkuListOption[]>(() => {
    return itemMetadata.skus.reduce<SkuListOption[]>((acc, sku) => {
      acc.push({
        metadata: {
          sku,
        },
        subtitle: (
          <ItemSubtitle
            baseUnit={sku.baseUnit}
            currencyCode={currencyCode || DEFAULT_CURRENCY_CODE}
            displayString={sku.packDisplayString}
            modifierString={sku.modifierString}
            promotionPriceInMicros={sku.promotionPricePerUnitInMicros}
            showInlineModifiers={false}
            unitCount={sku.unitCount}
            unitPriceInMicros={sku.unitPriceInMicros}
          />
        ),
        title: sku.externalID || '',
        value: sku.variantPackUUID,
      });
      return acc;
    }, []);
  }, [itemMetadata.skus, currencyCode]);

  return (
    <List<SkuListOption>
      {...listProps}
      data={skuSelectionListItems}
      renderMainContent={(item) => (
        <View style={styles.listItemContent}>
          <Text
            numberOfLines={1}
            style={[TYPOGRAPHY.xsmall, styles.listItemTitle]}
          >
            {item.title}
          </Text>
          {item.subtitle}
        </View>
      )}
    />
  );
};

const useStyles = createStyles((theme) => ({
  listItemContent: {
    flex: 1,
  },
  listItemTitle: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
  },
}));
